import { adminApi, adminSilentApi, userApi, userSilentApi } from "../../utils/apiInstances";
import { AuthContext, ApiResponse } from "../types";
import { getRecordValue, isRecord, unwrapApiRecord } from "../utils/apiResponse";

export type { AuthContext };

/**
 * Helper to get API instance based on context
 */
const getApi = (context: AuthContext, silent: boolean = true) => {
    if (context === "admin") return silent ? adminSilentApi : adminApi;
    // Organizer uses user API instances currently, as they are a type of user
    return silent ? userSilentApi : userApi;
};

/**
 * Helper to get base endpoint based on context and resource
 */
const getEndpoint = (context: AuthContext, resource: string) => {
    switch (context) {
        case "admin":
            return `/admin/admin-${resource}`;
        case "organizer":
            return `/organizer/${resource}`;
        case "user":
        default:
            return `/user/${resource}`;
    }
};

/**
 * Unified function to get Pitches
 */
export const handleGetPitches = async (
    context: AuthContext,
    params: Record<string, unknown> = {}
): Promise<ApiResponse> => {
    try {
        const query = Object.entries(params)
            .filter(([, value]) => value !== undefined && value !== null && value !== "")
            .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
            .join("&");

        const api = getApi(context);
        const endpoint = getEndpoint(context, "pitches");
        const queryStr = query ? `?${query}` : "";
        const uri = `${endpoint}${queryStr}`;

        const response = await api("GET", uri);
        return response;
    } catch (error) {
        console.error(`Error fetching pitches (${context}):`, error);
        throw error;
    }
};

/**
 * Organizer: Get Pitch Insights
 */
export const handleGetPitchInsights = async (
    context: AuthContext,
    params: Record<string, unknown> = {}
): Promise<ApiResponse> => {
    try {
        const query = Object.entries(params)
            .filter(([, value]) => value !== undefined && value !== null && value !== "")
            .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
            .join("&");

        const api = getApi(context);
        const endpoint = getEndpoint(context, "pitches");
        const queryStr = query ? `?${query}` : "";
        const uri = `${endpoint}/insights${queryStr}`;

        const response = await api("GET", uri);
        return response;
    } catch (error) {
        console.error(`Error fetching pitch insights (${context}):`, error);
        throw error;
    }
};

/**
 * Unified function to get Pitch by ID
 */
export const handleGetPitchById = async (context: AuthContext, pitchID: string | number) => {
    try {
        const api = getApi(context);
        const endpoint = getEndpoint(context, "pitches");
        const response = await api("GET", `${endpoint}/${pitchID}`);
        return response;
    } catch (error) {
        console.error(`Error fetching pitch by ID (${context}):`, error);
        throw error;
    }
};

/**
 * Create Pitch (Admin/Organizer)
 */
export const handleCreatePitch = async (
    context: AuthContext,
    payload: Record<string, unknown>,
    onSuccess?: (res: ApiResponse) => void,
    onError?: (err: unknown) => void
): Promise<ApiResponse> => {
    try {
        const api = getApi(context, false);
        const endpoint = getEndpoint(context, "pitches");
        const response = await api("POST", endpoint, payload);
        if (onSuccess) onSuccess(response);
        return response;
    } catch (error) {
        if (onError) onError(error);
        throw error;
    }
};

/**
 * Update Pitch (Admin/Organizer)
 */
export const handleUpdatePitch = async (
    context: AuthContext,
    pitchID: string | number,
    payload: Record<string, unknown>,
    onSuccess?: (res: ApiResponse) => void,
    onError?: (err: unknown) => void
): Promise<ApiResponse> => {
    try {
        const api = getApi(context, false);
        const endpoint = getEndpoint(context, "pitches");
        const response = await api("PUT", `${endpoint}/${pitchID}`, payload);
        if (onSuccess) onSuccess(response);
        return response;
    } catch (error) {
        if (onError) onError(error);
        throw error;
    }
};

/**
 * Unified Pitch Submission (Create or Edit)
 */
export interface PitchSubmitOptions {
    role: AuthContext;
    mode: 'create' | 'edit';
    pitchId?: string | number | undefined;
    payload: Record<string, unknown>;
    onSuccess?: (res: ApiResponse) => void;
    onError?: (err: unknown) => void;
}

export const handlePitchSubmit = async (options: PitchSubmitOptions): Promise<ApiResponse> => {
    const { role, mode, pitchId, payload, onSuccess, onError } = options;
    try {
        let response: ApiResponse;
        if (mode === 'create') {
            response = await handleCreatePitch(role, payload);
        } else {
            if (pitchId === undefined || pitchId === null) {
                throw new Error('Pitch ID is required for edit mode');
            }
            response = await handleUpdatePitch(role, pitchId, payload);
        }

        if (onSuccess) onSuccess(response);
        return response;
    } catch (error) {
        if (onError) onError(error);
        throw error;
    }
};

/**
 * Delete Pitch (Admin/Organizer)
 */
export const handleDeletePitch = async (
    context: AuthContext,
    pitchID: string | number,
    onSuccess?: (res: ApiResponse) => void,
    onError?: (err: unknown) => void
): Promise<ApiResponse> => {
    try {
        const api = getApi(context, false);
        const endpoint = getEndpoint(context, "pitches");
        const response = await api("DELETE", `${endpoint}/${pitchID}`);
        if (onSuccess) onSuccess(response);
        return response;
    } catch (error) {
        if (onError) onError(error);
        throw error;
    }
};

/**
 * Admin: Get Pitch Approvals
 */
export const handleGetPitchApprovals = async (params: Record<string, unknown> = {}): Promise<ApiResponse> => {
    try {
        const query = Object.entries(params)
            .filter(([, value]) => value !== undefined && value !== null && value !== "")
            .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
            .join("&");
        const queryStr = query ? `?${query}` : "";
        const response = await adminSilentApi("GET", `/admin/admin-pitch-approvals${queryStr}`);
        return response;
    } catch (error) {
        console.error("Error fetching pitch approvals:", error);
        throw error;
    }
};

/**
 * Admin: Review parameters
 */
export const handleReviewPitchApproval = async (
    pitchID: string | number,
    payload: Record<string, unknown>
): Promise<ApiResponse> => {
    try {
        const response = await adminApi("PATCH", `/admin/admin-pitch-approvals/${pitchID}`, payload);
        return response;
    } catch (error) {
        console.error("Error reviewing pitch approval:", error);
        throw error;
    }
};

/**
 * Public: Get Public Pitches
 */
export const handleGetPublicPitches = async (
    params: Record<string, unknown> = {}
): Promise<ApiResponse | undefined> => {
    try {
        const query = Object.entries(params)
            .filter(([, value]) => value !== undefined && value !== null && value !== "")
            .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
            .join("&");

        const queryStr = query ? `?${query}` : "";
        const response = await userSilentApi("GET", `/user/public-pitch${queryStr}`);
        return response;
    } catch (error) {
        console.error("Error fetching public pitches:", error);
        return undefined;
    }
};

/**
 * Public: Get Public Pitch By ID
 */
export const handleGetPublicPitchById = async (pitchID: string | number) => {
    try {
        const response = await userSilentApi("GET", `/user/public-pitch/${pitchID}`);
        return response;
    } catch (error) {
        console.error("Error fetching public pitch by ID:", error);
        throw error;
    }
};

/**
 * Public/User: Book Pitch (Check Availability/Create Booking)
 */
export const handleCreatePitchBooking = async (
    payload: Record<string, unknown>,
    onSuccess: (res: ApiResponse) => void,
    onError?: (err: unknown) => void
): Promise<ApiResponse | undefined> => {
    try {
        const response = await userApi("POST", "/user/pitch-bookings", payload);
        onSuccess(response);
        return response;
    } catch (error) {
        if (onError) onError(error);
        return undefined;
    }
};

/**
 * Admin: Get Appointments (Booked Pitches)
 */
export const handleGetAdminAppointments = async (
    params: Record<string, unknown> = {}
): Promise<ApiResponse | undefined> => {
    try {
        const query = Object.entries(params)
            .filter(([, value]) => value !== undefined && value !== null && value !== "")
            .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
            .join("&");

        const includeHistory =
            params.history === true ||
            params.history === "true" ||
            params.history === 1 ||
            params.history === "1";
        const endpoint = includeHistory ? "/admin/admin-appointment-history" : "/admin/admin-appointments";
        const queryStr = query ? `?${query}` : "";
        const response = await adminSilentApi("GET", `${endpoint}${queryStr}`);
        return response;
    } catch (error) {
        console.error("Error fetching admin appointments:", error);
        return undefined;
    }
};

/**
 * Admin: Appointments CRUD
 */
export const handleAdminAppointmentCRUD = {
    create: async (
        payload: Record<string, unknown>,
        onSuccess: (res: ApiResponse) => void,
        onError?: (err: unknown) => void
    ) => {
        try {
            const response = await adminApi("POST", "/admin/admin-appointments", payload);
            onSuccess(response as ApiResponse);
            return response;
        } catch (error) {
            if (onError) onError(error);
            return undefined;
        }
    },
    update: async (
        id: string | number,
        payload: Record<string, unknown>,
        onSuccess: (res: ApiResponse) => void,
        onError?: (err: unknown) => void
    ) => {
        try {
            const response = await adminApi("PATCH", `/admin/admin-appointments/${id}`, payload);
            onSuccess(response as ApiResponse);
            return response;
        } catch (error) {
            if (onError) onError(error);
            return undefined;
        }
    },
    delete: async (id: string | number, onSuccess: (res: ApiResponse) => void, onError?: (err: unknown) => void) => {
        try {
            const response = await adminApi("DELETE", `/admin/admin-appointments/${id}`);
            onSuccess(response as ApiResponse);
            return response;
        } catch (error) {
            if (onError) onError(error);
            return undefined;
        }
    },
    getById: async (id: string | number): Promise<ApiResponse | undefined> => {
        try {
            const response = await adminSilentApi("GET", `/admin/admin-appointments/${id}`);
            return response as ApiResponse;
        } catch (error) {
            console.error(error);
            return undefined;
        }
    }
};

/**
 * Get Extras
 */
export const handleGetExtras = async (context: AuthContext = 'user') => {
    try {
        const api = getApi(context);
        const endpoint = getEndpoint(context, "extras");
        const response = await api("GET", endpoint);
        return response;
    } catch (error) {
        console.error(`Error fetching extras (${context}):`, error);
        return undefined;
    }
};

/**
 * Check Pitch Availability
 */
export const handleGetPitchAvailability = async (
    context: AuthContext,
    payload: Record<string, unknown>,
    onSuccess: (res: ApiResponse['data']) => void,
    onError: (err: ApiResponse['data']) => void
): Promise<void> => {
    try {
        const api = getApi(context);
        const resource = "pitch/availability";
        let endpoint = getEndpoint(context, resource);

        if (context === 'user') {
            endpoint = "/user/pitch/availability";
        }

        const response = await api("POST", endpoint, payload);
        const responseData = unwrapApiRecord(response);
        const message = getRecordValue(responseData, "message");

        if (isRecord(message) && getRecordValue(message, "transaction")) {
            onSuccess(message);
        } else if (message !== undefined) {
            onError(message);
        } else {
            onSuccess(responseData);
        }
    } catch (error) {
        if (onError) onError(error);
    }
};
